import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  flushQueue,
  listQueue,
  removeFromQueue,
  retryNow,
  subscribe,
  type QueuedSubmission,
} from '@/lib/offline-queue'
import { useAuth } from '@/context/AuthContext'
import { useI18n } from '@/i18n/I18nProvider'

/** How often to retry while items are waiting. */
const SYNC_INTERVAL_MS = 60_000

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)

  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)

    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)

    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  return online
}

/**
 * Binds the offline queue to React and drives the automatic sync.
 *
 * Mounted once, in AppShell. The worker never has to think about the queue —
 * it drains itself as soon as there is a connection.
 */
export function useOfflineQueue() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isOnline = useOnlineStatus()

  const { t, tp } = useI18n()
  const [items, setItems] = useState<QueuedSubmission[]>([])
  const [isSyncing, setIsSyncing] = useState(false)

  const refresh = useCallback(async () => {
    if (!user) {
      setItems([])
      return
    }
    setItems(await listQueue(user.id))
  }, [user])

  // Any queue mutation, from anywhere in the app, refreshes this view.
  useEffect(() => {
    void refresh()
    return subscribe(() => void refresh())
  }, [refresh])

  const sync = useCallback(
    async (announce = false) => {
      if (!user || !navigator.onLine) return

      setIsSyncing(true)

      try {
        const result = await flushQueue(user.id)

        if (result.sent > 0) {
          toast.success(tp('queue.sent', result.sent))
          // The new submissions have to appear in the lists and the dashboard.
          queryClient.invalidateQueries({ queryKey: ['submissions'] })
          queryClient.invalidateQueries({ queryKey: ['analytics'] })
        }

        if (result.failed > 0) {
          toast.error(tp('queue.failed', result.failed))
        }

        if (announce && result.sent === 0 && result.failed === 0) {
          toast.info(t('queue.nothing'))
        }
      } finally {
        setIsSyncing(false)
      }
    },
    [user, queryClient, t, tp],
  )

  // Sync on login, whenever the connection returns, and periodically while
  // anything is still waiting.
  useEffect(() => {
    if (!user) return

    void sync()

    const onOnline = () => void sync()
    window.addEventListener('online', onOnline)

    const timer = setInterval(() => {
      if (navigator.onLine) void sync()
    }, SYNC_INTERVAL_MS)

    return () => {
      window.removeEventListener('online', onOnline)
      clearInterval(timer)
    }
  }, [user, sync])

  const discard = useCallback(
    async (id: string) => {
      await removeFromQueue(id)
      toast.success(t('queue.discarded'))
    },
    [t],
  )

  const retry = useCallback(
    async (id: string) => {
      await retryNow(id)
      await sync(true)
    },
    [sync],
  )

  return {
    items,
    pending: items.filter((item) => item.status === 'pending'),
    failed: items.filter((item) => item.status === 'failed'),
    isOnline,
    isSyncing,
    sync,
    discard,
    retry,
  }
}
